import React from 'react';
import EditableText from './EditableText';

// Shared logic for comment-style pages (Examples of Strengths, Development
// Areas Comments, Words of Wisdom). Sections are rendered in the order their
// comment arrays appear in the data, and each array's title comes from a
// matching key in `sectionHeaders`. The renderer is fully data-driven, so a new
// section (e.g. a `crossFunctionalComments` array + `crossFunctional` header)
// shows up automatically with no code change.
//
// The only wrinkle is that a header key and the key of its comments array don't
// always spell the section the same way:
//   - header `allothers`/`others`  <-> array `allothersComments`/`allothers`
//   - header `team`                <-> array `teamMemberComments`
// We bridge that without a hardcoded key list by normalising both sides
// (lowercase, drop a trailing `comments`/`comment`) and matching on exact
// equality first, then substring containment, which covers `team`~`teammember`
// and `others`~`allothers` and any future near-spelling.

// Normalise a header or array key to a comparable section token.
export function normalizeKey(key) {
  return String(key)
    .toLowerCase()
    .replace(/comments?$/, '');
}

// Find the `sectionHeaders` key whose section matches `commentsKey`. Returns
// undefined when no header matches (the comment array is then skipped).
export function findHeaderKey(commentsKey, sectionHeaders) {
  const target = normalizeKey(commentsKey);
  const headerKeys = Object.keys(sectionHeaders);
  return (
    headerKeys.find((k) => normalizeKey(k) === target) ||
    headerKeys.find((k) => {
      const n = normalizeKey(k);
      return n && target && (n.includes(target) || target.includes(n));
    })
  );
}

function SectionHeader({ colorClass, marginTop, configKey, sectionKey }) {
  return (
    <div
      className={`comments-section__header ${colorClass}`}
      style={marginTop ? { marginTop: 16 } : undefined}
    >
      <EditableText
        as="h4"
        path={`${configKey}.sectionHeaders.${sectionKey}`}
        variant="inline"
        stripInlineWrap
      />
    </div>
  );
}

/**
 * Builds a flat array of renderable items (section headers + individual comment
 * rows) so MultiPage can split at the exact comment boundary where the page
 * runs out of space.
 *
 * @param {object} data       Page data: comment arrays keyed by section + a
 *                            `sectionHeaders` map of section title HTML.
 * @param {string} configKey  Page discriminator used in EditableText paths.
 */
export function buildCommentSections(data, configKey) {
  const items = [];
  let hasPrevSection = false;

  function pushHeader(sectionKey, colorClass) {
    items.push(
      <SectionHeader
        key={`${colorClass}-header-${items.length}`}
        colorClass={colorClass}
        marginTop={hasPrevSection}
        configKey={configKey}
        sectionKey={sectionKey}
      />
    );
  }

  function pushComments(comments, colorClass) {
    comments.forEach((comment, idx) => {
      items.push(
        <div key={`${colorClass}-${items.length}-${idx}`} className="comments-section__item">
          <p>{typeof comment === 'string' ? comment.trim() : comment}</p>
        </div>
      );
    });
  }

  const sectionHeaders = data.sectionHeaders;
  if (!sectionHeaders) return items;

  // Render sections in the order their comment arrays appear in the data. For
  // each non-empty array we look up its title in `sectionHeaders`; arrays with
  // no matching header are skipped. `headerKey` is the real key in
  // sectionHeaders, used for the EditableText path; the color class is keyed off
  // the normalised section name so styling stays stable across spellings.
  Object.keys(data)
    .filter((k) => k !== 'sectionHeaders' && Array.isArray(data[k]) && data[k].length > 0)
    .forEach((commentsKey) => {
      const headerKey = findHeaderKey(commentsKey, sectionHeaders);
      if (!headerKey) return;

      const colorClass = `comments-header--${normalizeKey(headerKey)}`;

      // A section's comments may arrive as an array of group objects, each with
      // its own comments list (e.g. manager comments grouped by relationship);
      // flatten those into a single list of comment strings.
      const comments = data[commentsKey].flatMap((c) =>
        c && typeof c === 'object' && Array.isArray(c.comments) ? c.comments : [c]
      );
      if (comments.length === 0) return;

      pushHeader(headerKey, colorClass);
      pushComments(comments, colorClass);
      hasPrevSection = true;
    });

  return items;
}
